import { AddTask } from "@mui/icons-material";
import { Box, Button, Card, CardContent, CardHeader, Divider, Stack, Typography, useMediaQuery } from "@mui/material";
import { useEffect, useState } from "react";
import { useParams } from "react-router";
import {
  ContainerGrid,
  CreateIssueModal,
  CreateTaskItemModal,
  MaterialSymbolIcon,
  MaterialSymbols,
  ViewToggleButtonGroup,
} from "src/components";
import { useSession } from "src/hooks";
import { UserIssuesFilterButton, UserIssuesList, UserTaskItemsFilterButton, UserTaskItemsList } from "./components";
import { useUserImplementationSettings } from "./hooks";

interface IParams {
  userId: string;
}

const UserImplementationPage = () => {
  const { userId: userGuid } = useParams<keyof IParams>() as IParams;
  const { userId: sessionUserGuid } = useSession();
  const isMdUp = useMediaQuery((theme) => theme.breakpoints.up("md"));
  const { settings, updateSettings } = useUserImplementationSettings(userGuid);
  const [isTableView, setIsTableView] = useState(true);
  const [isCreateIssueModalOpen, setIsCreateIssueModalOpen] = useState(false);
  const [isCreateTaskItemModalOpen, setIsCreateTaskItemModalOpen] = useState(false);

  useEffect(() => {
    handleViewToggled(settings.tableView);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userGuid]);

  useEffect(() => {
    if (isMdUp) {
      setIsTableView(settings.tableView);
    } else {
      setIsTableView(false);
    }
  }, [isMdUp, userGuid, settings]);

  function handleViewToggled(value: boolean) {
    setIsTableView(value);
    updateSettings({
      tableView: value,
    });
  }

  return (
    <>
      <Stack spacing={1}>
        <Card>
          <CardContent>
            <Stack direction="row" spacing={1} sx={{ alignItems: "center", justifyContent: "space-between" }}>
              <Stack spacing={0.25}>
                <Typography variant="h5">Implementation</Typography>
                <Typography>Tasks and Loops</Typography>
              </Stack>
              {isMdUp && <ViewToggleButtonGroup isTableView={isTableView} onViewToggled={handleViewToggled} />}
            </Stack>
          </CardContent>
        </Card>
        <Box>
          <ContainerGrid container spacing={1}>
            <ContainerGrid size={{ xs: 12, lg: isTableView ? 12 : 6 }}>
              <Card>
                <CardHeader
                  title={<Typography variant="h6">Tasks</Typography>}
                  action={
                    <Stack direction="row" spacing={1} divider={<Divider orientation="vertical" flexItem />}>
                      {userGuid === sessionUserGuid && (
                        <Button
                          variant="tertiary"
                          startIcon={<AddTask />}
                          onClick={() => setIsCreateTaskItemModalOpen(true)}
                        >
                          Add New Task
                        </Button>
                      )}
                      <UserTaskItemsFilterButton userGuid={userGuid} />
                    </Stack>
                  }
                  sx={{ pb: 0, pr: 0.5 }}
                />
                <Box sx={{ p: 0.5 }}>
                  <UserTaskItemsList userGuid={userGuid} isTableView={isTableView} />
                </Box>
              </Card>
            </ContainerGrid>
            <ContainerGrid size={{ xs: 12, lg: isTableView ? 12 : 6 }}>
              <Card>
                <CardHeader
                  direction="row"
                  title={<Typography variant="h6">Loops</Typography>}
                  action={
                    <Stack direction="row" spacing={1} divider={<Divider orientation="vertical" flexItem />}>
                      {userGuid === sessionUserGuid && (
                        <Button
                          variant="tertiary"
                          startIcon={<MaterialSymbolIcon symbol={MaterialSymbols.QuickPhrases} />}
                          onClick={() => setIsCreateIssueModalOpen(true)}
                        >
                          Add New Loop
                        </Button>
                      )}
                      <UserIssuesFilterButton userGuid={userGuid} />
                    </Stack>
                  }
                  sx={{ pb: 0, pr: 0.5 }}
                />
                <Box sx={{ p: 0.5 }}>
                  <UserIssuesList userGuid={userGuid} isTableView={isTableView} />
                </Box>
              </Card>
            </ContainerGrid>
          </ContainerGrid>
        </Box>
      </Stack>

      <CreateTaskItemModal
        visible={isCreateTaskItemModalOpen}
        onCreated={() => setIsCreateTaskItemModalOpen(false)}
        onCancelled={() => setIsCreateTaskItemModalOpen(false)}
      />

      <CreateIssueModal
        visible={isCreateIssueModalOpen}
        onCreated={() => setIsCreateIssueModalOpen(false)}
        onCancelled={() => setIsCreateIssueModalOpen(false)}
      />
    </>
  );
};

export { UserImplementationPage };
